/*
 * Design note for this page:
 * Cybernetic Editorial Observatory.
 * Use asymmetric editorial composition, dark atmospheric surfaces, restrained deep-red accents,
 * and clear section pacing that reframes Command Secrets as a personal decision intelligence system.
 * Every section should reinforce disciplined clarity, premium structure, and protected methodology.
 */

import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { getLoginUrl } from "@/const";
import { Input } from "@/components/ui/input";
import { apiClient } from "@/lib/trpc";
import {
  AlertTriangle,
  ArrowRight,
  BookOpenText,
  BrainCircuit,
  ChevronRight,
  Fingerprint,
  Globe2,
  Languages,
  Monitor,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Target,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const heroImage =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663479039543/iUN34WhkzrGKnVArwsU4Fm/command-secrets-hero-observatory-Nq8ANhf4bZdiu8bgGKVBGV.webp";
const intelligenceMapImage =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663479039543/iUN34WhkzrGKnVArwsU4Fm/command-secrets-intelligence-map-45TnqamfYfPRGVqa4RbGAe.webp";
const premiumInterfaceImage =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663479039543/iUN34WhkzrGKnVArwsU4Fm/command-secrets-premium-interface-enXeyjSHcHGvD8NzjwLdnS.webp";
const ritualImage =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663479039543/iUN34WhkzrGKnVArwsU4Fm/command-secrets-signal-ritual-KiJSFxmt6cigK8MtQpMHDd.webp";

const navigation = [
  { label: "System", href: "#system" },
  { label: "Methods", href: "#methods" },
  { label: "Signals", href: "#signals" },
  { label: "Pricing", href: "#pricing" },
  { label: "Trust", href: "#trust" },
  { label: "Access", href: "#access" },
];

const proofMetrics = [
  { value: "9", label: "Reference archetypes visible across the public system" },
  { value: "4", label: "Languages prepared for worldwide users" },
  { value: "3", label: "Access surfaces: Android, iPhone, and Desktop" },
];

const intelligenceModules = [
  {
    title: "Track",
    text: "Log decisions, behaviour, and outcomes so repeat mistakes stop disguising themselves as random noise.",
  },
  {
    title: "Detect",
    text: "See your strengths, drivers, blind spots, and role-fit pressure inside one controlled intelligence surface.",
  },
  {
    title: "Correct",
    text: "Receive intervention guidance before the same misalignment repeats in work, relationships, or timing.",
  },
];

const intelligencePillars = [
  {
    icon: Target,
    title: "See what you are naturally good at",
    body: "The system highlights where you create results with less friction, more consistency, and a clearer sense of direction.",
  },
  {
    icon: BrainCircuit,
    title: "Understand what drives your choices",
    body: "Command Secrets surfaces the internal drivers behind ambition, pressure, recognition, security, autonomy, and repeated decision habits.",
  },
  {
    icon: AlertTriangle,
    title: "Catch the blind spots you cannot easily see",
    body: "The core value is not only personality language. It is the ability to reveal hidden error patterns, overuse tendencies, and costly self-misreads before they harden into outcomes.",
  },
  {
    icon: ShieldCheck,
    title: "Avoid roles that are structurally unaligned",
    body: "Not every capable person fits every role. The system is built to warn against placements that look attractive on paper but create repeating friction in real life.",
  },
];

const engines = [
  {
    name: "Decision Log",
    description:
      "Capture real choices, context, and outcomes so the system has disciplined behavioural material to read instead of vague self-description.",
  },
  {
    name: "Pattern Detection",
    description:
      "Surface recurring loops across timing, pressure, confidence, avoidance, and decision quality so users can see the structure behind repetition.",
  },
  {
    name: "Driver Index",
    description:
      "Clarify what truly motivates a person beneath surface preference, including push factors, reward orientation, and hidden pressure responses.",
  },
  {
    name: "Blind Spot Radar",
    description:
      "Identify what a person is likely to miss about themselves, including repeated overreach, underuse, and costly misread zones.",
  },
  {
    name: "Role Fit Guidance",
    description:
      "Translate the intelligence into suitable-role direction, work-style fit, and misalignment warnings for individuals and teams.",
  },
  {
    name: "Intervention Alerts",
    description:
      "Move beyond description with warnings, corrective prompts, and next-step guidance designed to stop avoidable repetition.",
  },
];

const trustLayers = [
  {
    icon: ShieldCheck,
    title: "Protected method boundaries",
    body: "Privacy language, access control, and proprietary-method protection are treated as product architecture so users can trust the output surface without seeing the protected calculation logic underneath.",
  },
  {
    icon: Globe2,
    title: "Worldwide delivery discipline",
    body: "The customer-facing experience is prepared for English, Chinese, Japanese, and Indonesian so the system can scale across regions without losing caution, clarity, or commercial seriousness.",
  },
  {
    icon: Fingerprint,
    title: "Structured outputs, not vague labels",
    body: "Users receive strengths, drivers, blind spots, fit direction, and solution guidance in a legible surface that feels rigorous, controlled, and commercially usable rather than generic.",
  },
];

const signalLayers = [
  {
    index: "01",
    title: "Know what you are good at",
    body: "The system gives consumers a legible strengths layer so ability is not confused with image, ambition, or wishful identity.",
  },
  {
    index: "02",
    title: "Know what drives you",
    body: "A useful reading explains why someone keeps choosing the same kinds of environments, opportunities, and risks.",
  },
  {
    index: "03",
    title: "Know your blind spots",
    body: "The strongest value comes from exposing what the person does not usually catch alone: repeated distortions, overuse patterns, and hidden derailers.",
  },
  {
    index: "04",
    title: "Know where you do not fit",
    body: "Role guidance must also include hard warnings. Some placements are fundamentally unaligned and should not be sold as motivational stretch.",
  },
];

const pricingPlans = [
  {
    name: "Free",
    price: "$0",
    tag: "Start here",
    response:
      "Decision profile from birth data, up to 15 decision logs, 1 basic pattern insight, and a logging streak tracker.",
    solution: "Reveal the issue, but not enough intelligence depth to resolve it.",
  },
  {
    name: "Core",
    price: "$19.90/month",
    tag: "Most popular",
    response:
      "Unlimited decision logs, full pattern detection across 4 engines, real-time intervention alerts, decision scoring, outcome replay, and a monthly intelligence brief.",
    solution: "Continuous monthly intelligence for users who want the system to keep watching the pattern before mistakes repeat.",
  },
  {
    name: "Pro",
    price: "$149/year",
    tag: "Serious operators",
    response:
      "Everything in Core, plus weekly pattern reports, category-specific insights, deeper history, advanced analytics, premium editorial briefings, and priority support.",
    solution: "Annual discipline support for users who want deeper reporting, stronger pattern memory, and a more structured change rhythm over time.",
  },
  {
    name: "Advanced",
    price: "$399 one-time",
    tag: "Lifetime",
    response:
      "Everything in Pro forever, with 10 AI-powered deep insights and permanent access to the lifetime pattern engine.",
    solution: "Permanent access for users who want to retain the system as a long-horizon personal intelligence asset.",
  },
];

const languageLayers = [
  {
    title: "English",
    native: "See your strengths, drivers, blind spots, and role fit.",
    body: "Primary command layer for global product clarity, app onboarding, and commercial terms.",
    note: "Built for users who want a premium intelligence surface without personality-test vagueness.",
  },
  {
    title: "Chinese",
    native: "看见你的优势、驱动力、盲点与适配位置。",
    body: "Prepared for customers who need serious interpretation and fit guidance in a major Asian language context.",
    note: "Designed so role fit, risk warnings, and decision guidance remain clear in Chinese.",
  },
  {
    title: "Japanese",
    native: "自分の強み、原動力、盲点、適した役割を見抜く。",
    body: "Prepared for precise communication where nuance, role suitability, and behavioural framing matter.",
    note: "Structured for readers who value caution, fit precision, and disciplined behavioural wording.",
  },
  {
    title: "Indonesian",
    native: "Lihat kekuatan, dorongan, blind spot, dan kecocokan peran Anda.",
    body: "Prepared for wider regional access so the intelligence system remains legible beyond a single-language market.",
    note: "Keeps the same premium logic available for Indonesian-speaking users across Southeast Asia.",
  },
];

const newsletterBenefits = [
  "Included in Core, Pro, and Advanced membership surfaces.",
  "Monthly intelligence briefs summarise active patterns, pressure points, and decision risks.",
  "Pro and Advanced layers deepen the cadence with stronger reporting, premium briefings, and longer memory.",
];

const accessSurfaces = [
  {
    icon: Smartphone,
    title: "Android",
    body: "Google Play-facing presentation is being aligned with the same premium intelligence identity used on the website and inside the product.",
  },
  {
    icon: Smartphone,
    title: "iPhone",
    body: "iPhone positioning is being prepared so the same system language, access logic, and subscription framing carry across Apple-facing materials.",
  },
  {
    icon: Monitor,
    title: "Desktop",
    body: "Immediate web access already supports serious evaluation, review, and subscription entry without weakening the premium brand surface.",
  },
];

const testimonials = [
  {
    quote:
      "The strongest part was not the label. It was seeing the blind spot behind a pattern I had been repeating for years.",
    role: "Blind-spot clarity promise",
  },
  {
    quote:
      "It felt different because it linked strengths, drivers, and misalignment risk instead of only describing a personality style.",
    role: "Role-fit positioning",
  },
  {
    quote:
      "The system matters most when it stops the next bad decision, not when it only explains the last one.",
    role: "Track → Detect → Correct",
  },
];

const policyLinks = [
  {
    label: "Disclaimer",
    href: "/legal/disclaimer",
    description: "Public figure references, estimated net worth examples, and automated-analysis boundaries.",
  },
  {
    label: "Privacy",
    href: "/legal/privacy",
    description: "Singapore PDPA-first privacy handling with global rights awareness.",
  },
  {
    label: "Singapore legal",
    href: "/legal/singapore",
    description: "Singapore-facing PDPA, communication, and support wording for local users and residents.",
  },
  {
    label: "UK legal",
    href: "/legal/uk",
    description: "United Kingdom transparency, rights, and support-handling wording for UK visitors.",
  },
  {
    label: "Cancellation",
    href: "/legal/cancellation",
    description: "Clear cancellation access with a strict no-refund rule after rendered analysis.",
  },
  {
    label: "Support",
    href: "/support",
    description: "Manual billing, upgrade, downgrade, report-delivery help, and a structured intake flow.",
  },
  {
    label: "Security",
    href: "/security",
    description: "Owner access control, anti-abuse posture, and resilience expectations.",
  },
];

const releaseStatusNotes = [
  {
    label: "Public website status",
    value: "Live",
    description: "The public site, legal surfaces, and support path are visible on the production domain.",
  },
  {
    label: "Policy surface status",
    value: "Expanded",
    description: "Singapore and UK legal subpages now sit alongside privacy, disclaimer, cancellation, support, and security pages.",
  },
  {
    label: "Android release watch",
    value: "Pending signing alignment",
    description: "The rebuilt app bundle is prepared, while Play upload progress remains dependent on valid upload-key alignment.",
  },
];

const releaseArchiveEntries = [
  {
    date: "2026-04-22",
    title: "Support and trust surfaces expanded",
    detail: "The public support intake flow, Singapore and UK legal pages, and the homepage release-status block were added to make legal and operational guidance easier to find.",
  },
  {
    date: "2026-04-21",
    title: "Privacy, cancellation, disclaimer, and security pages published",
    detail: "Core legal and trust pages were published so visitors can review privacy handling, refund boundaries, service security posture, and public-reference limitations in one place.",
  },
  {
    date: "2026-04-20",
    title: "Production domains aligned",
    detail: "The Command Secrets website was aligned to its live public domains so the release surface is no longer confined to preview-only access.",
  },
];

type PaidPlanKey = "core" | "pro" | "advanced";

export default function Home() {
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);

  useEffect(() => {
    document.title = "COMMAND Secrets – Decision Intelligence System";
  }, []);

  const handleNewsletterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!newsletterEmail.trim()) {
      toast.error("Enter an email to prepare your intelligence brief access.");
      return;
    }

    toast.success("Newsletter connection is being finalized. For now, use the membership plans below to enter the intelligence brief flow.");
    setNewsletterEmail("");
  };

  const handlePlanCheckout = async (planName: string) => {
    const normalizedPlan = planName.toLowerCase();

    if (normalizedPlan === "free") {
      window.location.href = "/access-options";
      return;
    }

    if (normalizedPlan !== "core" && normalizedPlan !== "pro" && normalizedPlan !== "advanced") {
      toast.error("This payment plan is not available yet.");
      return;
    }

    if (authLoading) {
      toast.info("Checking your account before checkout.");
      return;
    }

    let activeUser = user;

    if (!activeUser) {
      toast.info("Confirming your sign-in before checkout.");
      activeUser = await refreshAuth();
    }

    if (!activeUser) {
      toast.info("Please sign in before continuing to secure checkout.");
      window.location.href = getLoginUrl();
      return;
    }

    try {
      setCheckoutPlan(normalizedPlan);
      toast.info("Preparing secure checkout...");

      const result = await apiClient.billing.createCheckoutSession.mutate({
        plan: normalizedPlan as PaidPlanKey,
      });

      window.open(result.checkoutUrl, "_blank", "noopener,noreferrer");
      toast.success("Checkout opened in a new tab.");
    } catch (error) {
      console.error(error);
      toast.error("Checkout could not be created. Please try again.");
    } finally {
      setCheckoutPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="fixed inset-x-0 top-0 z-40 border-b border-white/12 bg-[rgba(4,6,10,0.94)] shadow-[0_16px_48px_rgba(0,0,0,0.38)] backdrop-blur-xl">
        <div className="container flex items-center justify-between gap-6 py-4">
          <a href="#top" className="flex items-center gap-3 text-sm tracking-[0.32em] text-white">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(255,255,255,0.14)] bg-[rgba(131,12,28,0.24)] shadow-[0_0_30px_rgba(131,12,28,0.3)]">
              <span className="font-display text-lg">C</span>
            </span>
            <span>
              COMMAND <span className="text-[var(--signal-soft)]">Secrets</span>
            </span>
          </a>

          <nav className="hidden items-center gap-6 lg:flex">
            {navigation.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-[11px] uppercase tracking-[0.3em] text-white/82 transition hover:text-white"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="hidden border-white/15 px-4 text-xs uppercase tracking-[0.24em] text-white/78 hover:bg-white/8 hover:text-white md:inline-flex"
              asChild
            >
              <a href="#trust">Proof</a>
            </Button>
            <Button
              className="bg-[var(--signal)] px-5 text-xs uppercase tracking-[0.24em] text-white hover:bg-[var(--signal-strong)]"
              asChild
            >
              <a href="#access">Get the app</a>
            </Button>
          </div>
        </div>
      </div>

      <main id="top" className="overflow-hidden pt-24">
        <section className="relative border-b border-white/8 pb-20 pt-12 md:pb-28 md:pt-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(130,18,34,0.18),transparent_26%),radial-gradient(circle_at_left_center,rgba(255,255,255,0.04),transparent_20%)]" />
          <div className="absolute inset-0 opacity-40" aria-hidden="true">
            <div className="noise-overlay h-full w-full" />
          </div>

          <div className="container relative grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] px-4 py-2 text-[11px] uppercase tracking-[0.32em] text-white/70">
                <Sparkles className="h-4 w-4 text-[var(--signal-soft)]" />
                Personal Decision Intelligence System
              </div>

              <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_170px]">
                <div>
                  <p className="eyebrow">Signal detected</p>
                  <h1 className="mt-4 max-w-3xl font-display text-5xl leading-[0.94] text-white sm:text-6xl xl:text-7xl">
                    Stop repeating misaligned decisions <span className="text-[var(--signal-soft)]">before they repeat you.</span>
                  </h1>
                  <p className="mt-8 max-w-2xl text-lg leading-8 text-white/72 md:text-xl">
                    Track your behaviour. See your patterns. COMMAND Secrets is a high-intelligence AI system powered by a proprietary algorithmic framework for people who need more than a personality label. It reveals strengths, drivers, blind spots, and role-fit risk so hidden logic becomes usable discipline.
                  </p>
                </div>

                <div className="hidden xl:block">
                  <div className="intel-pill">
                    <span className="block text-[10px] uppercase tracking-[0.34em] text-white/45">Mode</span>
                    <span className="mt-4 block font-display text-3xl text-white">Track → Detect → Correct</span>
                    <span className="mt-8 block text-sm leading-7 text-white/62">
                      Structured guidance for strengths, drivers, blind spots, role fit, and disciplined intervention.
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Button
                  size="lg"
                  className="bg-[var(--signal)] px-8 text-xs uppercase tracking-[0.28em] text-white hover:bg-[var(--signal-strong)]"
                  asChild
                >
                  <a href="#pricing">
                    Start Analysis <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/12 px-8 text-xs uppercase tracking-[0.28em] text-white/82 hover:bg-white/8 hover:text-white"
                  asChild
                >
                  <a href="#methods">Review system logic</a>
                </Button>
              </div>

              <div className="mt-14 grid gap-4 md:grid-cols-3">
                {proofMetrics.map((metric) => (
                  <div key={metric.label} className="metric-card">
                    <div className="text-4xl font-display text-white">{metric.value}</div>
                    <p className="mt-3 text-sm leading-6 text-white/60">{metric.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative lg:pt-6">
              <div className="absolute -left-8 top-8 hidden h-40 w-40 rounded-full bg-[rgba(145,19,36,0.25)] blur-3xl lg:block" />
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-3 shadow-[0_30px_120px_rgba(0,0,0,0.45)]">
                <img
                  src={heroImage}
                  alt="Command Secrets observatory visualization"
                  className="h-[560px] w-full rounded-[1.45rem] object-cover object-center"
                />
                <div className="absolute inset-x-8 bottom-8 rounded-[1.5rem] border border-white/12 bg-[rgba(10,12,18,0.74)] p-5 backdrop-blur-xl">
                  <p className="text-[10px] uppercase tracking-[0.34em] text-white/44">Operating principle</p>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    {intelligenceModules.map((module, index) => (
                      <div key={module.title} className="space-y-2 border-l border-white/10 pl-4">
                        <div className="text-xs uppercase tracking-[0.3em] text-[var(--signal-soft)]">0{index + 1}</div>
                        <h2 className="font-display text-lg text-white">{module.title}</h2>
                        <p className="text-sm leading-6 text-white/58">{module.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="system" className="relative border-b border-white/8 py-20 md:py-28">
          <div className="container grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="space-y-6">
              <p className="eyebrow">System logic</p>
              <h2 className="section-title">A disciplined intelligence environment for people who want to know what fits, what drives, and what silently derails.</h2>
              <p className="section-copy">
                COMMAND Secrets is designed to interpret more than personality. It helps users understand the relationship between behaviour, hidden motivators, blind spots, timing, and placement risk. The methodology is proprietary, so the platform exposes a clear output system without exposing the protected formula behind it.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {intelligencePillars.map((pillar, index) => {
                const Icon = pillar.icon;
                const offset = index === 0 ? "md:translate-y-8" : index === 3 ? "md:-translate-y-8" : "";
                return (
                  <div key={pillar.title} className={`editorial-card ${offset}`}>
                    <Icon className="h-6 w-6 text-[var(--signal-soft)]" />
                    <h3 className="mt-5 font-display text-2xl text-white">{pillar.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-white/62">{pillar.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="methods" className="relative border-b border-white/8 py-20 md:py-28">
          <div className="absolute inset-y-0 right-0 hidden w-[38%] bg-[linear-gradient(180deg,rgba(130,18,34,0.08),transparent)] lg:block" />
          <div className="container grid gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div className="space-y-6 lg:sticky lg:top-28">
              <p className="eyebrow">Analytical engines</p>
              <h2 className="section-title">Six signal layers. One controlled intelligence surface.</h2>
              <p className="section-copy">
                Each layer contributes a different kind of evidence, but the value of the system comes from disciplined synthesis. Command Secrets is not a pile of disconnected readings. It is a behavioural intelligence framework that translates multiple signals into a clearer decision picture.
              </p>
              <div className="overflow-hidden rounded-[1.75rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-3">
                <img
                  src={intelligenceMapImage}
                  alt="Abstract intelligence map representing Command Secrets analytical architecture"
                  className="h-[360px] w-full rounded-[1.2rem] object-cover"
                />
              </div>
              <div className="rounded-[1.5rem] border border-[rgba(185,57,76,0.26)] bg-[rgba(120,18,31,0.09)] p-5">
                <p className="text-[10px] uppercase tracking-[0.34em] text-[var(--signal-soft)]">Proprietary framework notice</p>
                <p className="mt-3 text-sm leading-7 text-white/68">
                  The public system explains what users receive, such as strengths, drivers, blind spots, and role guidance. The exact weighting logic, calculation order, and internal scoring rules remain protected intellectual property.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {engines.map((engine, index) => (
                <div key={engine.name} className="engine-card">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">Engine 0{index + 1}</span>
                    <ChevronRight className="h-4 w-4 text-[var(--signal-soft)]" />
                  </div>
                  <h3 className="mt-5 font-display text-[1.85rem] leading-tight text-white">{engine.name}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/62">{engine.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="signals" className="relative border-b border-white/8 py-20 md:py-28">
          <div className="container grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[rgba(255,255,255,0.02)] p-4 shadow-[0_25px_80px_rgba(0,0,0,0.35)]">
              <img
                src={premiumInterfaceImage}
                alt="Premium Command Secrets interface visualization"
                className="h-[560px] w-full rounded-[1.6rem] object-cover"
              />
              <div className="absolute inset-x-6 top-6 flex items-center justify-between rounded-full border border-white/12 bg-[rgba(7,9,13,0.66)] px-4 py-3 backdrop-blur-xl">
                <span className="text-[10px] uppercase tracking-[0.34em] text-white/44">Editorial observation</span>
                <span className="text-[10px] uppercase tracking-[0.34em] text-[var(--signal-soft)]">Strengths · Drivers · Blind Spots · Fit</span>
              </div>
            </div>

            <div className="space-y-7">
              <p className="eyebrow">Signal layers</p>
              <h2 className="section-title">A better decision begins when hidden logic becomes visible.</h2>
              <p className="section-copy">
                A credible intelligence system should tell people what they are good at, what drives them, where they keep misreading themselves, and which roles create structural fit or avoidable strain. That is how insight becomes commercially useful.
              </p>

              <div className="grid gap-4">
                {signalLayers.map((layer) => (
                  <div key={layer.index} className="stack-card">
                    <span className="stack-index">{layer.index}</span>
                    <div>
                      <h3 className="font-display text-2xl text-white">{layer.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-white/60">{layer.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="relative border-b border-white/8 py-20 md:py-28">
          <div className="container grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="space-y-6 lg:sticky lg:top-28">
              <p className="eyebrow">Payment levels</p>
              <h2 className="section-title">Different payment levels unlock different intelligence depth, response power, and solution outcomes.</h2>
              <p className="section-copy">
                The commercial model follows the same operational logic as the product itself. Entry access reveals the issue. Monthly access keeps the system watching the pattern with the user. Annual access deepens discipline through reporting and continuity. Lifetime access secures permanent retention of the highest consumer intelligence layer.
              </p>
              <div className="rounded-[1.5rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5">
                <p className="text-[10px] uppercase tracking-[0.34em] text-[var(--signal-soft)]">Solution framing</p>
                <p className="mt-3 text-sm leading-7 text-white/68">
                  Free reveals the issue. Core provides ongoing monthly intelligence and intervention. Pro builds annual discipline through deeper reporting and support. Advanced secures lifetime retention of the strongest long-horizon access layer.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {pricingPlans.map((plan) => (
                <div key={plan.name} className="engine-card min-h-[320px]">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">{plan.tag}</span>
                      <h3 className="mt-4 font-display text-[2rem] text-white">{plan.name}</h3>
                    </div>
                    <span className="rounded-full border border-[rgba(185,57,76,0.28)] bg-[rgba(120,18,31,0.12)] px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">
                      Response tier
                    </span>
                  </div>
                  <p className="mt-6 font-display text-4xl text-white">{plan.price}</p>
                  <p className="mt-6 text-sm leading-7 text-white/62">{plan.response}</p>
                  <div className="mt-6 border-t border-white/10 pt-5">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/42">Solution outcome</p>
                    <p className="mt-3 text-sm leading-7 text-white/72">{plan.solution}</p>
                  </div>
                  <Button
                    className="mt-6 w-full bg-[var(--signal)] text-xs uppercase tracking-[0.24em] text-white hover:bg-[var(--signal-strong)]"
                    onClick={() => {
                      void handlePlanCheckout(plan.name);
                    }}
                    disabled={checkoutPlan === plan.name.toLowerCase()}
                  >
                    {checkoutPlan === plan.name.toLowerCase()
                      ? "Opening checkout..."
                      : plan.name === "Free"
                        ? "Start free"
                        : `Choose ${plan.name}`}
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div className="container mt-8">
            <div className="rounded-[1.5rem] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] px-5 py-4 text-sm leading-7 text-white/60">
              All payments are non-refundable once analysis has been rendered. Cancellations may be permitted prior to analysis delivery.
            </div>
          </div>
        </section>

        <section id="trust" className="relative border-b border-white/8 py-20 md:py-28">
          <div className="absolute inset-0 opacity-70" aria-hidden="true">
            <img
              src={ritualImage}
              alt="Abstract ritual signal visualization representing protected Command Secrets analysis workflows"
              className="h-full w-full object-cover opacity-30"
            />
          </div>
          <div className="container relative grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
            <div className="space-y-6">
              <p className="eyebrow">Trust and access</p>
              <h2 className="section-title">Authority comes from protected methods, disciplined outputs, and worldwide clarity.</h2>
              <p className="section-copy">
                Premium interpretation requires more than atmosphere. It needs disciplined data language, protected method boundaries, clear commercial terms, and international legibility across device types and customer languages. That is how credibility is built without depending on a personality-led founder story.
              </p>
              <div className="pt-2">
                <Button
                  variant="outline"
                  className="border-white/12 px-8 text-xs uppercase tracking-[0.28em] text-white/82 hover:bg-white/8 hover:text-white"
                  asChild
                >
                  <a href="#methods">Review proof structure</a>
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {trustLayers.map((layer) => {
                const Icon = layer.icon;
                return (
                  <div key={layer.title} className="trust-card">
                    <Icon className="h-6 w-6 text-[var(--signal-soft)]" />
                    <h3 className="mt-5 font-display text-2xl text-white">{layer.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-white/62">{layer.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="relative border-b border-white/8 py-20 md:py-28">
          <div className="container grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="space-y-6 lg:sticky lg:top-28">
              <p className="eyebrow">Language intelligence</p>
              <h2 className="section-title">Global readiness means decision clarity survives translation.</h2>
              <p className="section-copy">
                Command Secrets should not look like a local product stretched internationally at the last minute. The language layer is designed so serious decision guidance can be presented in English, Chinese, Japanese, and Indonesian while preserving the same premium commercial structure and role-fit seriousness.
              </p>
                  <div className="rounded-[1.5rem] border border-[rgba(185,57,76,0.26)] bg-[rgba(120,18,31,0.09)] p-5">
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.34em] text-[var(--signal-soft)]">
                      <Languages className="h-4 w-4" />
                      Language surface
                    </div>
                    <p className="mt-3 text-sm leading-7 text-white/68">
                      Language support is presented as an intelligence-access layer, not a cosmetic toggle. The aim is to keep the same authority, caution, and intervention logic visible worldwide.
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {languageLayers.map((language, index) => (
                    <div key={language.title} className={`editorial-card ${index % 2 === 0 ? "md:translate-y-8" : ""}`}>
                      <span className="text-[11px] uppercase tracking-[0.3em] text-[var(--signal-soft)]">Language 0{index + 1}</span>
                      <h3 className="mt-5 font-display text-3xl text-white">{language.title}</h3>
                      <p className="mt-4 text-lg leading-8 text-white">{language.native}</p>
                      <p className="mt-4 text-sm leading-7 text-white/62">{language.body}</p>
                      <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-7 text-white/54">{language.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="border-b border-white/8 py-20 md:py-28">

          <div className="container grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="space-y-6">
              <p className="eyebrow">Reader impressions</p>
              <h2 className="section-title">The experience should feel precise, layered, and difficult to ignore.</h2>
              <p className="section-copy">
                The strongest reaction comes when a user recognizes a blind spot, a driver, or a role mismatch they had been rationalizing for too long. That feeling of recognition is what the system should amplify from first contact to subscription decision.
              </p>
            </div>

            <div className="grid gap-4">
              {testimonials.map((item) => (
                <blockquote key={item.quote} className="quote-card">
                  <p className="font-display text-2xl leading-[1.35] text-white/92">“{item.quote}”</p>
                  <footer className="mt-5 text-[11px] uppercase tracking-[0.28em] text-white/42">{item.role}</footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-white/8 py-20 md:py-28">
          <div className="container grid gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
            <div className="space-y-6">
              <p className="eyebrow">Newsletter intelligence</p>
              <h2 className="section-title">Membership includes a continuing intelligence brief, not just a one-time report.</h2>
              <p className="section-copy">
                Command Secrets is positioned as a living decision system, so the subscription surface should keep returning with fresh pattern warnings, pressure signals, and role-fit reminders. The newsletter layer reinforces retention by turning insight into a recurring operating rhythm.
              </p>
              <div className="rounded-[1.5rem] border border-[rgba(185,57,76,0.26)] bg-[rgba(120,18,31,0.09)] p-5 text-sm leading-7 text-white/72">
                Free reveals the issue. Core intervenes with monthly intelligence cadence. Pro builds discipline through stronger reporting and premium briefings. Advanced secures permanent access to the long-horizon intelligence layer.
              </div>
            </div>

            <div className="engine-card min-h-[unset] space-y-6">
              <div>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[var(--signal-soft)]">Included value</span>
                <h3 className="mt-4 font-display text-[2rem] leading-tight text-white">Monthly intelligence brief with premium escalation paths.</h3>
              </div>
              <div className="grid gap-3">
                {newsletterBenefits.map((item) => (
                  <div key={item} className="rounded-[1rem] border border-white/10 bg-[rgba(255,255,255,0.03)] px-4 py-3 text-sm leading-7 text-white/64">
                    {item}
                  </div>
                ))}
              </div>
              <form onSubmit={handleNewsletterSubmit} className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <Input
                  type="email"
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                  placeholder="Enter your email for membership updates"
                  className="h-12 rounded-[1rem] border-white/12 bg-[rgba(255,255,255,0.04)] px-4 text-white placeholder:text-white/36"
                />
                <Button type="submit" className="h-12 rounded-[1rem] bg-[var(--signal)] px-6 text-xs uppercase tracking-[0.24em] text-white hover:bg-[var(--signal-strong)]">
                  Prepare access
                </Button>
              </form>
              <p className="text-sm leading-7 text-white/50">
                The input flow is currently a front-end placeholder so the premium membership surface can be reviewed before live newsletter wiring is connected.
              </p>
            </div>
          </div>
        </section>

        <section id="access" className="py-20 md:py-28">
          <div className="container">
            <div className="relative overflow-hidden rounded-[2.4rem] border border-white/12 bg-[linear-gradient(135deg,rgba(12,15,21,0.96),rgba(25,9,13,0.92))] px-6 py-10 shadow-[0_40px_120px_rgba(0,0,0,0.45)] md:px-10 md:py-14">
              <div className="absolute inset-0 opacity-35" aria-hidden="true">
                <div className="noise-overlay h-full w-full" />
              </div>
              <div className="absolute -right-16 top-0 h-56 w-56 rounded-full bg-[rgba(145,19,36,0.22)] blur-3xl" />
              <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
                <div className="max-w-3xl">
                  <p className="eyebrow">Access layer</p>
                  <h2 className="mt-4 font-display text-4xl leading-tight text-white md:text-5xl">
                    Open Command Secrets with a clear access path, platform status, and next step.
                  </h2>
                  <p className="mt-6 max-w-2xl text-base leading-8 text-white/68 md:text-lg">
                    Immediate browser access is available now, while public Android and iPhone install routes are only shown when they are genuinely ready for user and reviewer use. That means the access journey now responds clearly instead of sending people into a silent loop. English is supported, with Chinese, Japanese, and Indonesian language delivery prepared for worldwide users. Members also receive continuing AI-generated insight cadence through pattern reports and newsletter-style intelligence updates.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                  <Button
                    size="lg"
                    className="bg-[var(--signal)] px-8 text-xs uppercase tracking-[0.28em] text-white hover:bg-[var(--signal-strong)]"
                    asChild
                  >
                    <a href="/access-options">
                      Open access options
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/12 px-8 text-xs uppercase tracking-[0.28em] text-white/82 hover:bg-white/8 hover:text-white"
                    asChild
                  >
                    <a href="#pricing">View plans</a>
                  </Button>
                </div>
              </div>

              <div className="relative mt-8 grid gap-4 border-t border-white/10 pt-6 md:grid-cols-3">
                {accessSurfaces.map((surface) => {
                  const Icon = surface.icon;
                  return (
                    <div
                      key={surface.title}
                      className="rounded-[1.2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-4 text-sm leading-7 text-white/64"
                    >
                      <Icon className="h-5 w-5 text-[var(--signal-soft)]" />
                      <h3 className="mt-4 font-display text-2xl text-white">{surface.title}</h3>
                      <p className="mt-3">{surface.body}</p>
                    </div>
                  );
                })}
              </div>

              <div className="relative mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-[1rem] border border-white/10 bg-[rgba(255,255,255,0.03)] px-4 py-3 text-sm leading-6 text-white/64">
                  Immediate browser access is live now, and the access-options page states native install availability honestly.
                </div>
                <div className="rounded-[1rem] border border-white/10 bg-[rgba(255,255,255,0.03)] px-4 py-3 text-sm leading-6 text-white/64">
                  Educational and illustrative use only. No affiliation with referenced public figures.
                </div>
                <div className="rounded-[1rem] border border-white/10 bg-[rgba(255,255,255,0.03)] px-4 py-3 text-sm leading-6 text-white/64">
                  Results and frameworks are protected intellectual property. No external sharing of protected analysis outputs.
                </div>
              </div>

              <div className="relative mt-8 border-t border-white/10 pt-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div className="max-w-2xl">
                    <p className="eyebrow">Public trust surfaces</p>
                    <h3 className="mt-3 font-display text-3xl text-white">Legal, privacy, regional notices, support, cancellation, and security pages are public and visible.</h3>
                    <p className="mt-3 text-sm leading-7 text-white/64">
                      These pages explain the boundaries around public-figure references, privacy handling, Singapore and UK legal wording, manual billing support, no-refund rules after rendered analysis, and the service's security posture.
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {policyLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="rounded-[1.2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-4 transition hover:border-[rgba(191,59,80,0.32)] hover:bg-[rgba(131,12,28,0.08)]"
                    >
                      <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">Open page</div>
                      <h4 className="mt-3 font-display text-2xl text-white">{link.label}</h4>
                      <p className="mt-3 text-sm leading-7 text-white/64">{link.description}</p>
                    </a>
                  ))}
                </div>

                <div className="mt-6 rounded-[1.5rem] border border-[rgba(191,59,80,0.22)] bg-[linear-gradient(180deg,rgba(131,12,28,0.12),rgba(255,255,255,0.02))] p-5 shadow-[0_24px_80px_rgba(0,0,0,0.18)] md:p-6">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-2xl">
                      <p className="eyebrow">Release watch / admin note</p>
                      <h4 className="mt-3 font-display text-3xl text-white">A public status block exists for future Play review and release updates.</h4>
                      <p className="mt-3 text-sm leading-7 text-white/64">
                        This section can be updated whenever release review, signing, listing, or policy status materially changes, so users are not left guessing about major operational shifts.
                      </p>
                    </div>
                    <a
                      href="/support"
                      className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.24em] text-[var(--signal-soft)] transition hover:text-white"
                    >
                      Raise an operational question
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  </div>

                  <div className="mt-6 grid gap-4 md:grid-cols-3">
                    {releaseStatusNotes.map((item) => (
                      <article key={item.label} className="rounded-[1.2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-4">
                        <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">{item.label}</p>
                        <p className="mt-3 font-display text-2xl text-white">{item.value}</p>
                        <p className="mt-3 text-sm leading-7 text-white/64">{item.description}</p>
                      </article>
                    ))}
                  </div>

                  <div className="mt-6 rounded-[1.4rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5 md:p-6">
                    <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                      <div className="max-w-2xl">
                        <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">Public changelog archive</p>
                        <h5 className="mt-3 font-display text-2xl text-white">Dated release notes for policy, support, and platform visibility updates.</h5>
                        <p className="mt-3 text-sm leading-7 text-white/64">
                          This archive can be extended whenever major support handling, policy wording, review status, or public access conditions materially change.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 space-y-4">
                      {releaseArchiveEntries.map((entry) => (
                        <article key={`${entry.date}-${entry.title}`} className="rounded-[1.15rem] border border-white/10 bg-[rgba(7,9,13,0.56)] p-4 md:p-5">
                          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <h6 className="font-display text-2xl text-white">{entry.title}</h6>
                            <span className="text-[11px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">{entry.date}</span>
                          </div>
                          <p className="mt-3 text-sm leading-7 text-white/64">{entry.detail}</p>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
